/**
 * Zapier-inspired Test section for ReadOnly step panels.
 * Gates on required fields, runs action testNode / trigger sample fetch, shows DataInspector.
 */
import React, { useMemo, useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  MenuItem,
  ListItemIcon,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@material-ui/icons/RadioButtonUncheckedRounded';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import ScheduleRoundedIcon from '@material-ui/icons/ScheduleRounded';
import {
  testNode,
  fetchTriggerChoices,
  createTrigger,
  elementUpdated,
  saveWorkflow,
  elementSelected,
  selectWorkflowLoading,
  selectWorkflowId,
  selectWorkflowElements,
} from '../../features/workflow/workflowSlice';
import {
  canTestStep,
  getMissingRequiredFieldLabels,
  isTestDataEmpty,
  stepRequiresAuth,
  hasAuth,
  getWizardStepState,
  getDefaultWizardStep,
} from '../helpers/stepReady';
import { isBuiltInTriggerApp } from '../../constants/triggerApps';
import DataInspector from './DataInspector';

const HEADER_BLUE = '#0069ea';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  progressItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    padding: '4px 8px',
    borderRadius: 6,
  },
  progressActive: {
    color: HEADER_BLUE,
    backgroundColor: 'rgba(0, 105, 234, 0.08)',
  },
  progressDone: {
    color: theme.palette.success.main,
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  hint: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.4,
  },
  footerCta: {
    marginTop: theme.spacing(2),
    width: '100%',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 15,
    padding: '10px 16px',
    borderRadius: 8,
  },
  footerCtaDisabled: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.disabled,
  },
  footerRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  resultBox: {
    marginTop: theme.spacing(1.5),
    maxHeight: 280,
    overflowY: 'auto',
    overflowX: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1),
  },
  successBanner: {
    backgroundColor: 'rgba(46, 125, 50, 0.08)',
    borderLeft: '4px solid #2e7d32',
    padding: theme.spacing(1.5),
    borderRadius: 4,
    marginBottom: theme.spacing(1.5),
  },
  errorBanner: {
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
    borderLeft: '4px solid #d32f2f',
    padding: theme.spacing(1.5),
    borderRadius: 4,
    marginBottom: theme.spacing(1.5),
  },
  choiceItem: {
    borderRadius: 6,
    marginBottom: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  choiceSelected: {
    borderColor: HEADER_BLUE,
    backgroundColor: 'rgba(0, 105, 234, 0.06)',
  },
}));

function ProgressPill({ label, state }) {
  const classes = useStyles();
  const icon =
    state === 'done' ? (
      <CheckCircleRoundedIcon style={{ fontSize: 16 }} />
    ) : state === 'active' ? (
      <ErrorOutlineRoundedIcon style={{ fontSize: 16, color: '#f9a825' }} />
    ) : (
      <ScheduleRoundedIcon style={{ fontSize: 16 }} />
    );
  return (
    <span
      className={`${classes.progressItem} ${
        state === 'done' ? classes.progressDone : state === 'active' ? classes.progressActive : ''
      }`}
    >
      {icon}
      {label}
    </span>
  );
}

function isTestError(testData) {
  if (!testData || typeof testData !== 'object') return false;
  if (Object.prototype.hasOwnProperty.call(testData, 'error')) return true;
  if (typeof testData.status_code === 'number' && testData.status_code >= 400) return true;
  return false;
}

/** Setup → Configure → Test pills — clickable when unlocked. */
export function StepProgressPills({ node, activeStep = 'setup', onStepChange }) {
  const classes = useStyles();
  const { setupDone, configureDone, testDone } = getWizardStepState(node);

  const steps = [
    { id: 'setup', label: 'Setup', unlocked: true, done: setupDone },
    { id: 'configure', label: 'Configure', unlocked: setupDone, done: configureDone },
    { id: 'test', label: 'Test', unlocked: configureDone, done: testDone },
  ];

  const stateFor = (step) => {
    if (step.done) return activeStep === step.id ? 'done-active' : 'done';
    if (activeStep === step.id) return 'active';
    if (!step.unlocked) return 'locked';
    return 'pending';
  };

  return (
    <Box className={classes.progressRow} style={{ marginBottom: 12 }}>
      {steps.map((step, i) => {
        const state = stateFor(step);
        const isActive = activeStep === step.id;
        const isDone = state === 'done' || state === 'done-active';
        const icon = isDone ? (
          <CheckCircleRoundedIcon style={{ fontSize: 16 }} />
        ) : state === 'active' ? (
          <ErrorOutlineRoundedIcon style={{ fontSize: 16, color: '#f9a825' }} />
        ) : (
          <ScheduleRoundedIcon style={{ fontSize: 16 }} />
        );
        return (
          <React.Fragment key={step.id}>
            {i > 0 ? (
              <Typography variant="caption" color="textSecondary">→</Typography>
            ) : null}
            <button
              type="button"
              disabled={!step.unlocked}
              onClick={() => {
                if (step.unlocked && typeof onStepChange === 'function') onStepChange(step.id);
              }}
              className={`${classes.progressItem} ${
                isDone
                  ? classes.progressDone
                  : state === 'active'
                    ? classes.progressActive
                    : ''
              }`}
              style={{
                border: 'none',
                cursor: step.unlocked ? 'pointer' : 'not-allowed',
                opacity: step.unlocked ? 1 : 0.45,
                fontFamily: 'inherit',
                backgroundColor: isActive ? 'rgba(0, 105, 234, 0.08)' : 'transparent',
              }}
              aria-current={isActive ? 'step' : undefined}
            >
              {icon}
              {step.label}
            </button>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

/**
 * @param {'action'|'trigger'} mode
 * @param {object} node - workflow node
 * @param {string} nodeId
 * @param {function} [onEditFields] - open Edit fields when blocked on required
 * @param {function} [onContinue] - close panel when there is no next step
 */
const StepTestSection = forwardRef(function StepTestSection({ mode, node, nodeId, onEditFields, onContinue }, ref) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const loading = useSelector(selectWorkflowLoading);
  const workflowId = useSelector(selectWorkflowId);
  const elements = useSelector(selectWorkflowElements) || [];
  const [localBusy, setLocalBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  const d = node?.data || {};
  const fields = d.fields || [];
  const testData = d.testData;
  const hasTestData = !isTestDataEmpty(testData);
  const canTest = canTestStep(node);
  const missingLabels = useMemo(() => getMissingRequiredFieldLabels(fields), [fields]);
  const performList = d.performList;
  const triggerChoices = Array.isArray(d.triggerChoices) ? d.triggerChoices : [];
  const triggerlabel = d.triggerlabel || d.triggerLabel || d.actionlabel || d.actionLabel || d.action || 'Sample';
  const appLabel = d.label || d.app || d.type || 'App';
  const eventLabel =
    mode === 'trigger'
      ? (d.triggerlabel || d.triggerLabel || d.actionlabel || d.actionLabel || d.action || 'event')
      : (d.actionlabel || d.actionLabel || d.action || 'event');
  const isBuiltIn = isBuiltInTriggerApp(d.app);

  const isTesting =
    localBusy ||
    loading === 'testing' ||
    loading === 'fetchingTriggerChoices' ||
    loading === 'creatingTrigger';

  const persistNode = (patch) => {
    dispatch(elementUpdated(nodeId, { ...d, ...patch }, null));
    dispatch(saveWorkflow(1));
  };

  const selectTriggerChoice = (choice, index) => {
    persistNode({
      testData: choice,
      triggerchoice: `${triggerlabel} ${index + 1}`,
      triggerChoiceTime: Date.now(),
    });
  };

  const handleTestAction = async () => {
    if (!canTest || !nodeId) return;
    setLocalError(null);
    setLocalBusy(true);
    try {
      dispatch(elementUpdated(nodeId, { ...d, actionTestTime: Date.now() }, null));
      const result = await dispatch(testNode(nodeId));
      if (result.error) {
        setLocalError(result.error?.message || result.payload?.error || 'Test failed');
      } else {
        dispatch(saveWorkflow(1));
      }
    } catch (e) {
      setLocalError(e?.message || 'Test failed');
    } finally {
      setLocalBusy(false);
    }
  };

  const handleTestTrigger = async () => {
    if (!canTest || !nodeId) return;
    setLocalError(null);
    setLocalBusy(true);
    try {
      if (performList && !isBuiltIn) {
        const result = await dispatch(
          fetchTriggerChoices({ skip: 0, limit: 5, append: false, nodeId })
        );
        if (result.error) {
          setLocalError(result.error?.message || 'Could not fetch sample data');
          return;
        }
        const choices = result.payload?.triggerChoices || [];
        if (choices.length > 0) {
          persistNode({
            testData: choices[0],
            triggerchoice: `${triggerlabel} 1`,
            triggerChoiceTime: Date.now(),
            triggerChoices: choices,
          });
        } else {
          setLocalError('No recent samples found. Try again after new activity in the app.');
        }
        return;
      }

      // Webhook / schedule / other: createTrigger then seed testData when possible
      const result = await dispatch(createTrigger(workflowId));
      if (result.error) {
        setLocalError(result.error?.message || 'Could not set up trigger test');
        return;
      }
      const message = result.payload?.triggerMessage;
      if (message) {
        const patch = { triggerMessage: message };
        // Schedule (and similar) use triggerMessage as sample output
        if (d.app === 'schedule' || (isBuiltIn && d.app !== 'webhook')) {
          patch.testData = typeof message === 'object' ? message : { message };
          patch.triggerchoice = `${triggerlabel} 1`;
          patch.triggerChoiceTime = Date.now();
        }
        persistNode(patch);
      }
    } catch (e) {
      setLocalError(e?.message || 'Test failed');
    } finally {
      setLocalBusy(false);
    }
  };

  const handlePrimary = () => {
    if (mode === 'action') handleTestAction();
    else handleTestTrigger();
  };

  const runTestRef = useRef(handlePrimary);
  useEffect(() => {
    runTestRef.current = handlePrimary;
  });

  useImperativeHandle(ref, () => ({
    runTest: () => {
      if (runTestRef.current) runTestRef.current();
    },
  }), []);

  const primaryLabel = (() => {
    if (!canTest) return 'To continue, finish required fields';
    if (isTesting) return mode === 'action' ? 'Testing…' : 'Finding samples…';
    if (hasTestData) return mode === 'action' ? 'Retest action' : 'Retest trigger';
    return mode === 'action' ? 'Test action' : 'Test trigger';
  })();

  const testError = hasTestData && isTestError(testData);
  const testSucceeded = hasTestData && !testError && !isTesting;

  const findNextStep = () => {
    if (!nodeId || !Array.isArray(elements)) return null;
    const edge = elements.find((el) => el.source === nodeId && el.target);
    if (!edge) return null;
    return elements.find((el) => el.id === edge.target && !el.source && !el.target) || null;
  };

  const handleContinue = () => {
    const next = findNextStep();
    if (next) {
      // Open the next step so the user can finish unfinished setup, or review if already ready
      dispatch(elementSelected(next.id));
      return;
    }
    if (typeof onContinue === 'function') onContinue();
  };

  const continueLabel =
    mode === 'trigger' && triggerChoices.length > 0
      ? 'Continue with selected record'
      : 'Continue';

  return (
    <Box className={classes.root} id={nodeId ? `step-test-${nodeId}` : undefined}>
      <Typography className={classes.sectionLabel}>Test</Typography>
      <Typography variant="body2" className={classes.hint}>
        {mode === 'action'
          ? `Run ${eventLabel} in ${appLabel} with your mapped fields. The response becomes sample data for later steps.`
          : `Pull a recent sample for ${eventLabel} so later steps can map fields from real data.`}
      </Typography>

      {!canTest && (
        <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 8, marginBottom: 8 }}>
          <Typography variant="body2" color="textSecondary" style={{ flex: '1 1 auto', minWidth: 0 }}>
            {missingLabels.length > 0
              ? `Still needed: ${missingLabels.slice(0, 4).join(', ')}${missingLabels.length > 4 ? '…' : ''}`
              : stepRequiresAuth(node) && !hasAuth(node)
                ? 'Connect an account first.'
                : 'Finish setup and required fields first.'}
          </Typography>
          {onEditFields && missingLabels.length > 0 ? (
            <Button
              size="small"
              color="primary"
              onClick={onEditFields}
              style={{ textTransform: 'none', flexShrink: 0, padding: '0 4px', whiteSpace: 'nowrap' }}
            >
              Edit fields
            </Button>
          ) : null}
        </Box>
      )}

      {isTesting && (
        <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 12 }}>
          <CircularProgress size={22} color="primary" />
          <Typography variant="body2" color="textSecondary">
            {mode === 'action' ? `Sending a test to ${appLabel}…` : 'Looking for recent samples…'}
          </Typography>
        </Box>
      )}

      {localError && (
        <Box className={classes.errorBanner}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>Error</Typography>
          <Typography variant="body2">{localError}</Typography>
        </Box>
      )}

      {mode === 'trigger' && triggerChoices.length > 0 && (
        <Box style={{ marginBottom: 12 }}>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
            Choose a sample
          </Typography>
          {triggerChoices.map((item, i) => {
            const selected = d.triggerchoice === `${triggerlabel} ${i + 1}`;
            return (
              <MenuItem
                key={i}
                className={`${classes.choiceItem} ${selected ? classes.choiceSelected : ''}`}
                onClick={() => selectTriggerChoice(item, i)}
              >
                <ListItemIcon style={{ minWidth: 32 }}>
                  {selected ? (
                    <CheckCircleRoundedIcon color="primary" fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedRoundedIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <Typography variant="body2">{triggerlabel} {i + 1}</Typography>
              </MenuItem>
            );
          })}
        </Box>
      )}

      {mode === 'trigger' && d.triggerMessage && typeof d.triggerMessage === 'object' && d.app === 'webhook' && (
        <Box className={classes.successBanner}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>Webhook ready</Typography>
          <Typography variant="body2" color="textSecondary">
            Send a request to your webhook URL, then retest or use Advanced editing to pick a payload.
          </Typography>
          {d.triggerMessage.url || d.triggerMessage.webhookUrl ? (
            <Typography variant="caption" style={{ wordBreak: 'break-all', display: 'block', marginTop: 4 }}>
              {d.triggerMessage.url || d.triggerMessage.webhookUrl}
            </Typography>
          ) : null}
        </Box>
      )}

      {hasTestData && !isTesting && (
        <>
          <Box className={testError ? classes.errorBanner : classes.successBanner}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {testError ? 'Test returned an error' : 'Test succeeded'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {testError
                ? `Review the response from ${appLabel} below.`
                : `Sample data from ${eventLabel} is available for mapping in later steps.`}
            </Typography>
          </Box>
          <Box className={classes.resultBox}>
            <DataInspector data={testData} finalStep={!!testError} />
          </Box>
        </>
      )}

      <Divider style={{ margin: '16px 0 12px' }} />

      {testSucceeded ? (
        <Box className={classes.footerRow}>
          <Button
            className={classes.footerCta}
            variant="contained"
            color="primary"
            onClick={handleContinue}
            fullWidth
            style={{ marginTop: 0 }}
          >
            {continueLabel}
          </Button>
          <Button
            className={classes.footerCta}
            variant="outlined"
            color="primary"
            disabled={!canTest || isTesting}
            onClick={handlePrimary}
            fullWidth
            style={{ marginTop: 0 }}
          >
            {mode === 'action' ? 'Retest action' : 'Retest trigger'}
          </Button>
        </Box>
      ) : (
        <Button
          className={`${classes.footerCta} ${!canTest ? classes.footerCtaDisabled : ''}`}
          variant="contained"
          color="primary"
          disabled={!canTest || isTesting}
          onClick={handlePrimary}
          fullWidth
        >
          {isTesting ? (
            <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
          ) : null}
          {primaryLabel}
        </Button>
      )}
    </Box>
  );
});

export default StepTestSection;

// Re-export wizard helpers for any existing imports from this module
export { getWizardStepState, getDefaultWizardStep } from '../helpers/stepReady';
